import { useState } from 'react';
import { field } from '../create/Field';

/**
 * The `key` an uncontrolled, commit-on-blur box needs so a value the server never took cannot
 * stay on screen — and the bump that makes it work.
 *
 * `defaultValue` is read once per mount, so an uncontrolled input CANNOT show a value it did
 * not start with. Keying on the value remounts the box when the server's answer moves, so a
 * rejected save is SEEN. That alone is not enough: keying on `value` only remounts when the
 * number MOVES, and the two commonest wrong inputs don't move it — "abc" parses to NaN and
 * never commits, and "5.001" against a stored 5 rounds back to 5 and is skipped as unchanged.
 * Either way the box goes on displaying something the server never took. So the revision is
 * bumped on EVERY blur, committed or not, and every blur redisplays the canonical value.
 *
 * 🔴 **Extracted because it was written once and needed five times.** `NumberInput` had this;
 * the late-fee percent, the flat amount, the minimum balance and the reminder schedule — four
 * boxes in the SAME card, three of them money a shop charges its own customers — each had the
 * value-only key and neither bump. §7's one-of-N-parallel-paths, inside the ticket about it,
 * found by the Codex adversary. A hook is the fix that cannot be applied to only one of them.
 */
export function useCommitKey(value: string | number): [string, () => void] {
  const [rev, setRev] = useState(0);
  return [`${rev}:${value}`, () => setRev((r) => r + 1)];
}

/**
 * A whole number that commits on blur, not on every keystroke.
 *
 * On-change would fire a save per digit, so typing "30" over "5" would briefly persist 3.
 * Blur means the number that lands is the number the person finished typing — which matters
 * here because every one of these settings is a day count on an automation that emails
 * somebody's customers.
 *
 * `defaultValue` rather than `value`: the input owns the text while it is being edited, and
 * the server owns it the rest of the time. Making it controlled would fight the cursor.
 */
export function NumberInput({
  value,
  label,
  onCommit,
  disabled = false,
}: {
  value: number;
  label: string;
  onCommit: (n: number) => void;
  /** A role that cannot save this must not be handed a box that edits it. */
  disabled?: boolean;
}) {
  const [commitKey, redisplay] = useCommitKey(value);
  return (
    <input
      // ⚠️ Load-bearing, and not a list key — see `useCommitKey`.
      key={commitKey}
      className={field}
      aria-label={label}
      inputMode="numeric"
      disabled={disabled}
      defaultValue={String(value)}
      onBlur={(e) => {
        const n = Math.round(Number(e.target.value));
        // Bump on EVERY blur, committed or not — see `useCommitKey`.
        redisplay();
        if (Number.isFinite(n) && n !== value) onCommit(n);
      }}
    />
  );
}

/**
 * The same box for a value the product stores in HUNDREDTHS — cents, or basis points.
 *
 * A component rather than four hand-rolled `<input>`s, for the reason `NumberInput` is one:
 * the boxes it replaces are a late-fee percentage, a flat fee, and the balance below which a
 * shop won't chase — and every one of them had the value-only key, so a typo sat on screen
 * looking saved.
 *
 * `decimals` is the only thing that differs between money and a percentage: `$5.00` wants two
 * places always, `1.5%` wants none it doesn't need.
 */
export function HundredthsInput({
  value,
  label,
  decimals,
  onCommit,
  disabled = false,
}: {
  /** Cents, or basis points. */
  value: number;
  label: string;
  /** Fixed decimal places for display. Omit for the shortest honest rendering. */
  decimals?: number;
  onCommit: (hundredths: number) => void;
  disabled?: boolean;
}) {
  const shown =
    decimals === undefined ? (value / 100).toString() : (value / 100).toFixed(decimals);
  const [commitKey, redisplay] = useCommitKey(shown);
  return (
    <input
      key={commitKey}
      className={field}
      aria-label={label}
      inputMode="decimal"
      disabled={disabled}
      defaultValue={shown}
      onBlur={(e) => {
        const hundredths = Math.round(Number(e.target.value) * 100);
        redisplay();
        if (Number.isFinite(hundredths) && hundredths !== value) onCommit(hundredths);
      }}
    />
  );
}

/**
 * A list of whole numbers, one per line — the reminder schedule, and nothing else so far.
 *
 * Same law, a textarea instead of an input: it was keyed on the joined value, so a line of
 * gibberish (filtered out, list unchanged) or a re-ordering that sorts back to the same set
 * left text on screen the server never accepted.
 */
export function OffsetLinesInput({
  value,
  label,
  onCommit,
  disabled = false,
}: {
  value: number[];
  label: string;
  onCommit: (days: number[]) => void;
  disabled?: boolean;
}) {
  const [commitKey, redisplay] = useCommitKey(value.join(','));
  return (
    <textarea
      key={commitKey}
      className="min-h-[80px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
      aria-label={label}
      disabled={disabled}
      defaultValue={value.join('\n')}
      onBlur={(e) => {
        const days = e.target.value
          .split('\n')
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isFinite(n));
        redisplay();
        if (days.length > 0 && days.join(',') !== value.join(',')) onCommit(days);
      }}
    />
  );
}
